/*
 * yojo-analytics-events.js — cart event layer (DOM events + inert dataLayer).
 *
 * 2026-08-23 tracking audit: the theme no longer loads GTM or the Flashy
 * snippet (single owner per platform: Shopify app pixels + Flashy app
 * ScriptTag). The `cart:add` / `cart:update` DOM events below remain the
 * contract for theme UI (component-cart.js, yojo-delivery-zone.js). The
 * dataLayer pushes are kept for local debugging only — nothing reads them in
 * production. Do NOT add third-party senders here. See docs/TRACKING.md.
 *
 * --- original header (historical) ---
 *
 * ROOT CAUSE this fixes (2026-06-19 audit): the theme never dispatched `cart:add`
 * or `cart:update`, so Flashy's listeners (which the snippet binds) never fired,
 * and GTM received ZERO ecommerce events on the dataLayer. This module observes
 * cart mutations (fetch + XHR to /cart/add|change|update|clear) WITHOUT altering
 * the request/response, then:
 *   - dispatches `cart:add` / `cart:update` (revives Flashy AddToCart/UpdateCart)
 *   - pushes GA4-shaped `add_to_cart` / `view_item` / `begin_checkout` to dataLayer
 *
 * Observe-only + fully guarded (try/catch everywhere, never blocks the cart).
 *
 * FLASHY PAYLOAD RULE (2026-07-02, audit Part III §14 "reading 'length'"):
 * cart events reach Flashy ONLY via the `cart:add` / `cart:update` DOM events —
 * snippets/yojo-flashy.liquid translates those into canonical bodies
 * ({ content_ids: [...], value, currency }). Do NOT call window.flashy('AddToCart'|
 * 'UpdateCart') directly from here. Earlier builds double-sent them with
 * non-canonical bodies (AddToCart {items} / UpdateCart {}); for visitors Flashy
 * treats as anonymous, thunder.js persists raw events in localStorage.flashy_store
 * and later replays them in cart.val() reading body.content_ids.length UNGUARDED —
 * one malformed entry threw "Uncaught (in promise) TypeError: ... reading 'length'"
 * on every subsequent cart event on every PDP/cart page, and the throw aborted
 * cart.update() before flashy_cart_store was written, so it never self-healed.
 *
 * !!! NEEDS LIVE VERIFICATION via `shopify theme dev` before relying on the data:
 *     watch `window.dataLayer` + Flashy network beacons on add-to-cart / checkout.
 */
(function () {
  'use strict';
  if (window.__yojoEcomEvents) return;
  window.__yojoEcomEvents = true;

  var DATA = window.yojoAnalyticsData || {};
  var CURRENCY = DATA.currency || 'ILS';

  function dl(obj) { try { window.dataLayer = window.dataLayer || []; window.dataLayer.push(obj); } catch (e) {} }
  function emit(name, detail) { try { document.dispatchEvent(new CustomEvent(name, { detail: detail || {} })); } catch (e) {} }
  function money(cents) { return (typeof cents === 'number') ? Math.round(cents) / 100 : null; }

  function toGA(item) {
    return {
      item_id: String(item.product_id || item.id || ''),
      item_name: item.product_title || item.title || '',
      item_variant: item.variant_title || undefined,
      item_brand: item.vendor || undefined,
      price: money(item.price),
      quantity: item.quantity || 1
    };
  }

  function onAdd(json) {
    try {
      var items = (json && Array.isArray(json.items)) ? json.items : (json && json.id ? [json] : []);
      if (!items.length) return;
      var gaItems = items.map(toGA);
      var value = gaItems.reduce(function (s, i) { return s + (i.price || 0) * (i.quantity || 1); }, 0);
      dl({ ecommerce: null }); // clear previous ecommerce object (GA4 best practice)
      dl({ event: 'add_to_cart', ecommerce: { currency: CURRENCY, value: value, items: gaItems } });
      emit('cart:add', items.length === 1 ? items[0] : { items: items });
      // NO direct flashy('AddToCart') here — cart:add above drives the canonical
      // send in yojo-flashy.liquid. See FLASHY PAYLOAD RULE in the header.
    } catch (e) {}
  }

  function onChange() {
    try {
      emit('cart:update', {});
      dl({ event: 'cart_update' });
      // NO direct flashy('UpdateCart') here — cart:update above drives the
      // canonical send in yojo-flashy.liquid. See FLASHY PAYLOAD RULE.
    } catch (e) {}
  }

  // ---- Flashy poison heal (one-time per visitor, 2026-07-02) ----
  // Visitors who browsed while the malformed senders were live may still carry a
  // poisoned event log in localStorage.flashy_thunder (thunder.js's persisted
  // event store, base64(encodeURIComponent(JSON)) — verified live 2026-07-02).
  // Thunder.js only replays that log when flashy_cart_store is ABSENT, and the
  // resulting TypeError prevents flashy_cart_store from ever being written — so
  // without this heal they keep crashing forever. If we detect a poisoned
  // UpdateCart/AddToCart entry while the replay path is live, seed a neutral
  // flashy_cart_store so cart.val() short-circuits; thunder overwrites it with
  // real values on the next well-formed cart event. Read-only on the event log;
  // no-op on anything odd.
  try {
    if (!window.localStorage.getItem('flashy_cart_store')) {
      var rawLog = window.localStorage.getItem('flashy_thunder');
      if (rawLog) {
        var log = JSON.parse(decodeURIComponent(atob(rawLog)));
        var poisoned = Array.isArray(log) && log.some(function (entry) {
          return entry && (entry.event === 'UpdateCart' || entry.event === 'AddToCart') &&
            !(entry.body && Array.isArray(entry.body.content_ids));
        });
        if (poisoned) {
          window.localStorage.setItem('flashy_cart_store', '{"value":0,"total_products":0}');
        }
      }
    }
  } catch (e) { /* never block analytics init */ }

  var RE_ADD = /\/cart\/add(\.js)?(\?|$)/;
  var RE_CHANGE = /\/cart\/(change|update|clear)(\.js)?(\?|$)/;

  // ---- fetch (observe-only via response.clone) ----
  if (typeof window.fetch === 'function') {
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var p = _fetch.apply(this, arguments);
      try {
        if (RE_ADD.test(url)) {
          p.then(function (res) { try { if (res.ok) res.clone().json().then(onAdd).catch(function () {}); } catch (e) {} }).catch(function () {});
        } else if (RE_CHANGE.test(url)) {
          p.then(function (res) { if (res.ok) onChange(); }).catch(function () {});
        }
      } catch (e) {}
      return p;
    };
  }

  // ---- XMLHttpRequest (observe-only via load listener) ----
  try {
    var _open = XMLHttpRequest.prototype.open;
    var _send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) { this.__yojoUrl = url || ''; return _open.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      try {
        xhr.addEventListener('load', function () {
          try {
            var url = xhr.__yojoUrl || '';
            if (xhr.status >= 200 && xhr.status < 300) {
              if (RE_ADD.test(url)) { onAdd(JSON.parse(xhr.responseText)); }
              else if (RE_CHANGE.test(url)) { onChange(); }
            }
          } catch (e) {}
        });
      } catch (e) {}
      return _send.apply(this, arguments);
    };
  } catch (e) {}

  // ---- view_item (PDP) + begin_checkout (intent) ----
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  ready(function () {
    try {
      if (DATA.product && DATA.product.id) {
        var pr = DATA.product;
        dl({ ecommerce: null });
        dl({ event: 'view_item', ecommerce: { currency: CURRENCY, value: money(pr.price), items: [{ item_id: String(pr.id), item_name: pr.title, price: money(pr.price) }] } });
      }
      document.addEventListener('click', function (ev) {
        try {
          var t = ev.target;
          if (!t || !t.closest) return;
          if (t.closest('[name="checkout"], button[name="checkout"], a[href*="/checkout"], #CheckOut, .yojo-cart__checkout, [data-checkout-button]')) {
            dl({ event: 'yojo_checkout_intent' });
          }
        } catch (e) {}
      }, true);
    } catch (e) {}
  });
})();
